import React, { CSSProperties, FunctionComponent } from 'react';

import { Edito as EditoType } from '@gojob/cms';
import Flex from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';

import BlockRenderer from '../../../BlockRenderer';
import BreakpointContainer from '../../../BreakpointContainer';
import Buttons from '../../../Buttons';
import CheeryTitle from '../../../CheeryTitle';
import Image from './Image';

export interface EditoProps extends EditoType {
  reverse?: boolean;
  imgStyle?: CSSProperties;
  className?: string;
}

const useStyles = () => {
  const { breakpoint, spacing } = useTheme();

  return {
    root: {
      boxSizing: 'border-box',
    },
    withImage: {
      marginTop: spacing(4),
    },
    content: {
      boxSizing: 'border-box',
      flex: '1 0 50%',
      marginTop: 0,
      [breakpoint.up('md')]: {
        maxWidth: `calc(50% - ${spacing(12)})`,
        width: 'auto',
      },
    },
  } as const;
};

const Edito: FunctionComponent<EditoProps> = (props) => {
  const { className, reverse, content, image, ctas } = props;

  const { title, level = 'h3' } = props.title || {};
  const { alt, hotspot } = props.image?.image || {};
  const gatsbyImageData = image?.image?.asset?.localFile?.childImageSharp?.gatsbyImageData;

  const classes = useStyles();

  return (
    <Flex
      width="100%"
      justifyContent="center"
      flexDirection={{ xs: 'column', md: reverse ? 'row-reverse' : 'row' }}
      alignItems="center"
      css={[classes.root, className]}
      data-testid="edito-root"
    >
      {gatsbyImageData && (
        <Image
          hideOverlay={!image?.title}
          title={image?.title}
          hotspot={hotspot}
          image={gatsbyImageData}
          alt={alt}
        />
      )}

      {content ? (
        <BreakpointContainer
          maxWidth="lg"
          width="100%"
          css={[classes.content, gatsbyImageData && classes.withImage]}
          padding={[gatsbyImageData ? 4 : 0, 4]}
          flexDirection="column"
        >
          {title && (
            <CheeryTitle variant="headline2" as={level} gutter={4}>
              {title}
            </CheeryTitle>
          )}
          <BlockRenderer blocks={content} />
          <Flex width="100%" justifyContent="center">
            <Buttons ctas={ctas} />
          </Flex>
        </BreakpointContainer>
      ) : null}
    </Flex>
  );
};

export default Edito;
